import React, { useContext, useMemo } from 'react'
import AwesomeError from 'components/Common/AwesomeError'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import BodyText from 'components/Luxkit/Typography/BodyText'
import ContactContext from 'contexts/contactContext'
import TextButton from 'components/Luxkit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextLink from 'components/Luxkit/TextLink'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { EmptyArray } from 'lib/array/arrayUtils'
import useModalElementContext from 'hooks/Modal/useModalElementContext'

interface Props {
  className?: string;
  hasError?: boolean;
  rooms: Array<App.RoomOccupants>;
  activeHeaderFilter?: OfferPageFilterType;
}

function OfferCalendarNotAvailableMessage(props: Props) {
  const { hasError, className, rooms, activeHeaderFilter } = props
  const currentContact = useContext(ContactContext)
  const dispatch = useOfferPageDispatch()
  const modalContext = useModalElementContext()

  const isMobileDevice = useIsMobileDevice()
  const isNewMobileMultipleRoomEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.croNewMobileMultipleRoom)
  const showNewMobileMultipleRoom = useMemo(() => isMobileDevice && isNewMobileMultipleRoomEnabled && rooms.length > 1, [isMobileDevice, isNewMobileMultipleRoomEnabled, rooms])

  const onViewPackagesClick = () => {
    if (showNewMobileMultipleRoom) {
      if (activeHeaderFilter === 'checkin') {
        modalContext.resolve()
        dispatch({ type: OfferPageActions.closeHeaderFilter })
      }
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'multiRoomSelect' })
    } else {
      dispatch({ type: OfferPageActions.toggleSubFilter, filter: 'room' })
    }
  }

  return (
    <AwesomeError
      title={hasError ? 'Oh no, an error occurred' : 'No dates available'}
      className={className}
    >
      <VerticalSpacer gap={32}>
        <BodyText variant="large">
          {hasError && 'Our team are working on fixing it now.'}
          {!hasError && 'Unfortunately this room has sold out.'}
          {' '}
          Try selecting another room type or give our customer support team a call on{' '}
          <div>
            <TextLink
              href={`tel:${currentContact.local.number}`}
              rel="nofollow"
              wrap="no-wrap"
            >
              {currentContact.local.humanReadable}
            </TextLink>
          </div>
        </BodyText>
        <TextButton kind="primary" onClick={onViewPackagesClick}>
          View other rooms
        </TextButton>
      </VerticalSpacer>
    </AwesomeError>
  )
}

type MappedProps = Pick<Props, 'rooms' | 'activeHeaderFilter'>

export default withOfferPageAndReduxState((state): MappedProps => ({
  rooms: state.rooms ?? EmptyArray as Array<App.RoomOccupants>,
  activeHeaderFilter: state.activeHeaderFilter,
}), OfferCalendarNotAvailableMessage)
